import React, { useEffect, useState } from "react";
import {
  GET_ALL_GAMERATES,
  GET_ALL_STARLINE_GAMES,
  GET_ALL_JACKPOT_GAMES,
} from "../../service/admin.service";
import { Newspaper, Star, Trophy } from "lucide-react";
import { BsDice1 } from "react-icons/bs";

const GameRateSection = () => {
  const [matkaData, setMatkaData] = useState([]);
  const [starlineData, setStarlineData] = useState([]);
  const [jackpotData, setJackpotData] = useState([]);

  const [loadingMatka, setLoadingMatka] = useState(true);
  const [loadingStarline, setLoadingStarline] = useState(true);
  const [loadingJackpot, setLoadingJackpot] = useState(true);

  const [activeTab, setActiveTab] = useState("matka");

  /* ================= FETCH DATA ================= */

  const getMatkaRates = async () => {
    setLoadingMatka(true);
    const res = await GET_ALL_GAMERATES();

    const triplePana = {
      _id: "677b81a7d31824691abfffe2",
      gameName: "Triple Pana",
      gamePrice: 600,
      modifiedAt: "2025-01-08 13:10:27",
    };

    const index = res.data.findIndex((item) => item.gameName === "Double Pana");
    if (index !== -1) {
      res.data.splice(index + 1, 0, triplePana);
    }

    const uniqueData = res.data.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.gameName === item.gameName),
    );

    setMatkaData(uniqueData || []);
    setLoadingMatka(false);
  };

  const getStarlineRates = async () => {
    setLoadingStarline(true);
    const res = await GET_ALL_STARLINE_GAMES();
    setStarlineData(res.data || []);
    setLoadingStarline(false);
  };

  const getJackpotRates = async () => {
    setLoadingJackpot(true);
    const res = await GET_ALL_JACKPOT_GAMES();
    setJackpotData(res.data || []);
    setLoadingJackpot(false);
  };

  useEffect(() => {
    getMatkaRates();
    getStarlineRates();
    getJackpotRates();
  }, []);

  /* ================= UI HELPERS ================= */

  const iconColors = [
    "tw-bg-blue-100 tw-text-blue-600",
    "tw-bg-green-100 tw-text-green-600",
    "tw-bg-purple-100 tw-text-purple-600",
    "tw-bg-yellow-100 tw-text-yellow-600",
    "tw-bg-teal-100 tw-text-teal-600",
    "tw-bg-red-100 tw-text-red-600",
    "tw-bg-cyan-100 tw-text-cyan-600",
    "tw-bg-pink-100 tw-text-pink-600",
  ];

  const getTabTitle = () => {
    switch (activeTab) {
      case "matka":
        return {
          title: "Matka Rates",
          subtitle: "Standard Market Betting Rates",
        };
      case "starline":
        return {
          title: "Starline Rates",
          subtitle: "Starline Market Betting Rates",
        };
      case "jackpot":
        return {
          title: "Jackpot Rates",
          subtitle: "Jackpot Market Betting Rates",
        };
      default:
        return {
          title: "Matka Rates",
          subtitle: "Standard Market Betting Rates",
        };
    }
  };

  const { title, subtitle } = getTabTitle();

  /* ================= SKELETON ================= */

  const SkeletonCard = () => (
    <div className="tw-bg-white tw-rounded-xl tw-px-4 tw-py-3 tw-shadow-sm tw-flex tw-items-center tw-justify-between tw-animate-pulse">
      <div className="tw-flex tw-items-center tw-gap-4">
        <div className="tw-w-11 tw-h-11 tw-rounded-xl tw-bg-gray-200" />
        <div className="tw-space-y-2">
          <div className="tw-h-4 tw-w-32 tw-bg-gray-200 tw-rounded" />
          <div className="tw-h-3 tw-w-16 tw-bg-gray-100 tw-rounded" />
        </div>
      </div>
      <div className="tw-h-7 tw-w-20 tw-bg-gray-300 tw-rounded-lg" />
    </div>
  );

  const renderSkeletonGrid = () => (
    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 md:tw-gap-x-12 lg:tw-gap-x-20">
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );

  /* ================= CARDS ================= */

  const renderCards = (data) => (
    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 md:tw-gap-x-12 lg:tw-gap-x-20">
      {data?.map((item, index) => (
        <div
          key={item._id}
          className="tw-bg-white tw-rounded-xl tw-px-4 tw-py-3
          tw-shadow-sm hover:tw-shadow-md tw-transition-all
          tw-flex tw-items-center tw-justify-between"
        >
          <div className="tw-flex tw-items-center tw-gap-4">
            <div
              className={`tw-w-11 tw-h-11 tw-rounded-xl tw-flex tw-items-center tw-justify-center ${
                iconColors[index % iconColors.length]
              }`}
            >
              <BsDice1 className="tw-w-5 tw-h-5" />
            </div>

            <div>
              <h4 className="tw-font-semibold tw-text-gray-900">
                {item.gameName || item.providerName}
              </h4>
              <p className="tw-text-xs tw-text-gray-500">Rate</p>
            </div>
          </div>

          <div className="tw-bg-black tw-text-white tw-px-5 tw-py-1.5 tw-rounded-lg tw-font-semibold tw-text-sm tw-whitespace-nowrap">
            1 Ka {item.gamePrice || item.winAmount}
          </div>
        </div>
      ))}
    </div>
  );

  /* ================= JSX ================= */

  return (
    <div
      id="rates"
      className="tw-py-12 tw-px-4 tw-mt-10  sm:tw-px-6 lg:tw-px-12"
    >
      <div className="tw-max-w-7xl tw-mx-auto">
        {/* HEADER */}
        <div className="tw-text-center tw-mb-10">
          <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-bold tw-mb-3">
            Best Rates In The <span className="tw-text-[#0088ff]">Market</span>
          </h2>
          <p className="tw-text-gray-600 tw-max-w-2xl tw-mx-auto">
            We offer the most competitive rates with guaranteed payouts.
          </p>
        </div>

        {/* TABS */}
        <div className="tw-bg-[#1e6ebe] tw-rounded-t-2xl tw-p-2 sm:tw-p-4 tw-mb-8">
          <div className="tw-grid tw-grid-cols-3 tw-gap-2 lg:tw-flex lg:tw-justify-center">
            {[
              { key: "matka", label: "Matka", icon: Newspaper },
              { key: "starline", label: "Starline", icon: Star },
              { key: "jackpot", label: "Jackpot", icon: Trophy },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`tw-w-full lg:tw-w-auto tw-px-4 tw-py-2.5 tw-rounded-xl tw-font-semibold
                tw-flex tw-items-center tw-justify-center tw-gap-2 tw-transition-all
                ${
                  activeTab === key
                    ? "tw-bg-white tw-text-[#1e6ebe]"
                    : "tw-text-white hover:tw-bg-white hover:tw-text-[#1e6ebe]"
                }`}
              >
                <Icon className="tw-w-4 tw-h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* TAB TITLE */}
        <div className="tw-mb-6">
          <h3 className="tw-text-2xl tw-font-bold">{title}</h3>
          <p className="tw-text-gray-500 tw-text-sm">{subtitle}</p>
        </div>

        {/* CONTENT */}
        {activeTab === "matka" &&
          (loadingMatka ? renderSkeletonGrid() : renderCards(matkaData))}

        {activeTab === "starline" &&
          (loadingStarline ? renderSkeletonGrid() : renderCards(starlineData))}

        {activeTab === "jackpot" &&
          (loadingJackpot ? renderSkeletonGrid() : renderCards(jackpotData))}
      </div>
    </div>
  );
};

export default GameRateSection;
